import { EventEmitter } from './EventEmitter';
import { settings } from '@/config';
import { createLogger } from '@/utils/logger';
import type { TickState } from '@/types';

const log = createLogger('time');

/**
 * Single source of truth for animation timing. Owns the one and only
 * requestAnimationFrame loop and emits a `tick` every frame. Everything that
 * needs to update (renderer, camera, scroll, etc.) subscribes here, so the app
 * has exactly one clock.
 */
export class Time extends EventEmitter<{ tick: TickState }> {
  private startMs = performance.now();
  private currentMs = this.startMs;
  private rafId = 0;
  private running = false;

  readonly state: TickState = { delta: 0, elapsed: 0, elapsedMs: 0, frame: 0 };

  start(): void {
    if (this.running) return;
    this.running = true;
    this.currentMs = performance.now();
    this.rafId = requestAnimationFrame(this.loop);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  private loop = (now: number): void => {
    if (!this.running) return;
    const deltaMs = now - this.currentMs;
    this.currentMs = now;

    this.state.delta = Math.min(deltaMs / 1000, settings.time.maxDelta);
    this.state.elapsedMs = now - this.startMs;
    this.state.elapsed = this.state.elapsedMs / 1000;
    this.state.frame += 1;

    // `EventEmitter.emit` has no error isolation — an exception thrown by ANY tick
    // listener (rendering, scroll, physics, ...) would otherwise propagate straight
    // out of this rAF callback and skip the re-schedule below, permanently freezing
    // the entire app on a single bad frame with no visible error (2026-07-05 —
    // investigating a report of the app "breaking" on a UI interaction). Isolate it:
    // log and keep ticking, so one bad frame can't silently kill everything forever.
    try {
      this.emit('tick', this.state);
    } catch (err) {
      log.error('Uncaught error in tick listener — continuing next frame.', err);
    }
    this.rafId = requestAnimationFrame(this.loop);
  };

  dispose(): void {
    this.stop();
    this.clearListeners();
  }
}
